import { flexRender, type Header } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";
import { SortIndicator } from "./SortIndicator";
import { TableColumnFilter } from "./TableColumnFilter";
import { getCellSizeStyles } from "./utils/getBodyCellProps";

interface TableHeaderCellProps<TData> {
  header: Header<TData, unknown>;
}

export function TableHeaderCell<TData>({
  header,
}: TableHeaderCellProps<TData>) {
  const canSort = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const filterConfig = header.column.columnDef.meta?.filter;
  const { style, innerStyle } = getCellSizeStyles(header.column);

  return (
    <th
      style={style}
      aria-sort={
        sortDirection === "asc"
          ? "ascending"
          : sortDirection === "desc"
            ? "descending"
            : "none"
      }
      tabIndex={canSort ? 0 : undefined}
      data-testid={canSort ? `sort-header-${header.column.id}` : undefined}
      onClick={header.column.getToggleSortingHandler()}
      onKeyDown={
        canSort
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                header.column.toggleSorting();
              }
            }
          : undefined
      }
      className={twMerge(
        "sticky top-0 z-10 border-b-2 bg-[#F1F1F3] px-4.5 py-3 text-left text-[11px] leading-4 font-bold tracking-wide text-[#1D2939] uppercase first:pl-[30px] last:pr-[30px]",
        canSort && "cursor-pointer select-none",
        header.column.columnDef.meta?.headerClassName,
      )}
    >
      <span className="inline-flex items-center" style={innerStyle}>
        <span
          className={twMerge(
            "inline-flex items-center gap-1",
            canSort && "group",
          )}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {canSort && <SortIndicator direction={sortDirection} />}
        </span>
        {filterConfig && <TableColumnFilter column={header.column} />}
      </span>
    </th>
  );
}
